
import { CacheManager, Utils } from "../utils";
import { PaymentMethod } from "../constants/PaymentMethod";
import { NoseurObject, TypeChecker, Toaster, Scheme, MessageSchemesIcons } from "@rivtn/noseur";
import ffs, { Ffs, HttpConfig, RequestProcessor, RequestType, Utils as KyofuucUtils, Interceptor } from "kyofuuc";
import { Subscriber } from "../utils/Subscriber";

export enum CardProcessor {

    EPI = "EPI",
    SIM = "SIM",

}

export interface ApiResponseData<T> {
    data?: T;
    content?: T[];
    size?: number;
    empty?: boolean;
    transformed?: any;
    total_pages?: number;
    total_elements?: number;
    number_of_elements?: number;
    pageable?: {
        paged: boolean;
        offset: number;
        page_size: number;
        page_number: number;
    };
};

export interface ApiResponse<T> {
    message: string;
    success: boolean;
    data?: ApiResponseData<T>;
    secondary_data: NoseurObject<any>;
};

export type SanitizedResponse<T> = {
    sanitized: T;
    status: number;
    errors?: any[];
    errorMessage: string;
    apiResponse: ApiResponse<T>;
}

export interface WalletUssdBankProcessorOrMobileMoneyOperator {
    code: string;
    name: string;
    logo_location: string;
}

export interface PaymentOptions {
    method: PaymentMethod;
    momo_operators?: WalletUssdBankProcessorOrMobileMoneyOperator[];
    wallet_processors?: WalletUssdBankProcessorOrMobileMoneyOperator[];
    ussd_bank_processors?: WalletUssdBankProcessorOrMobileMoneyOperator[];
}

export interface ITransaction {
    pd: any;
    tax: number;
    mcrk: string;
    amount: number;
    msisdn: string;
    status: number;
    discount: number;
    reference: string;
    amount_paid: number;
    payer_email: string;
    expiry_date: string;
    description: string;
    service_name: string;
    service_code: string;
    charge_amount: number;
    language_code: string;
    transaction_id: string;
    request_amount: number;
    payer_last_name: string;
    payer_first_name: string;
    charge_country_code: string;
    charge_currency_code: string;
    payer_account_number: string;
    card_processor: CardProcessor;
    payment_options: PaymentOptions[];
    redirects: {
        success: string;
        failure: string;
        partial: string;
    };
    customer: {
        email: string;
        last_name: string;
        first_name: string;
    };
    configuration: {
        can_change_msisdn: boolean;
        can_change_country: boolean;
        can_change_language: boolean;
        auto_redirect_wait_timeout: number;
        default_payment_method: PaymentMethod;
        redirect_logic: "NO_ACTION" | "USER_ACTION" | "AUTO_REDIRECT" | "USER_ACTION_AUTO_REDIRECT";
    };
}

export interface ITransactionCharge {
    mcrk: string;
    processor: string;
    result: NoseurObject<any>;
    extra_data: NoseurObject<any>;
}

export interface ITransactionChargePayment {
    platform_status: string;
    processor_status: string;
}

export class PloutousService {

    // remove /json route after add formData submission on kyofuuc, and remove the headers this.config
    config: HttpConfig = {
        headers: {
            // use obfuscation that can disect and mangle randomely every request and add unused values at randome locations
            Authorization: `Bearer ${process.env.REACT_APP_PLOUTOUS_SERVICE_CLIENT_TOKEN}`
        }
    };
    transport: Ffs;
    interceptor = new Interceptor();
    expressClientRequestKey: string = "";
    baseUrl: string = process.env.REACT_APP_PLOUTOUS_BASE_API_URL ?? "http://127.0.0.1:9600";

    protected static instance: PloutousService;

    constructor(baseUrl?: string) {
        this.interceptor.registerPostResponse((config: any, __: any, response: any) => {
            if (response.data?.data?.mcrk) {
                this.expressClientRequestKey = response.data.data.mcrk;
            }
            if (response.data?.data?.reference && response.data?.data?.transaction_id && response.data?.data?.payment_options?.length) {
                Subscriber.publish("UPDATED_TRANSACTION_OBJECT", response.data?.data);
            }
        });
        this.transport = ffs.init({
            baseUrl: baseUrl ?? this.baseUrl,
            interceptor: this.interceptor,
            ...this.config,
        });
    }

    static getInstance() {
        if (!PloutousService.instance) PloutousService.instance = new PloutousService();
        return PloutousService.instance;
    }

    normalizeParams(params: NoseurObject<any>) {
        if (!params.page) params.page = 1;
        if (!params.size) params.size = 10;
        if (!params.sort) params.sort = "id,DESC";
        Object.keys(params).forEach((key: string) => {
            const value = params[key];
            if (!TypeChecker.isArray(value)) return;
            params[key] = value.reduce((acc: string, v: string, index: number) => {
                if (index > 0) return `${acc},${key}=${v}`;
                return `${v}`;
            }, "");
        });
        return params;
    }

    report<T>(promise: Promise<any>, postResolve?: Function, postReject?: Function): Promise<SanitizedResponse<any>> {
        return new Promise((resolve, reject) => {
            promise.then((response: T) => {
                let transformedRespomse = this.buildResponse(response);
                if (postResolve) postResolve(transformedRespomse);
                resolve(transformedRespomse);
            }).catch((error: any) => {
                let transformedError = this.buildError(error);
                if (postReject) postReject(transformedError);
                reject(transformedError);
            });
        });
    }

    buildResponse<T>(response: any): SanitizedResponse<T> {
        if (!response.data || !response.data.data) return response;
        response.status = response?.status;
        response.apiResponse = response.data;
        response.sanitized = response.apiResponse.data;
        return response;
    }

    buildError<T>(error: any): SanitizedResponse<T> {
        if (!error) error = {};
        error.message = error?.response?.data?.message ?? error?.response?.message ?? error?.message ?? "An error occur please try again later";
        error.errors = error?.response?.data?.errors;
        error.errorMessage = error.message;
        error.status = error.response?.status ?? -1;
        return error;
    }

    buildUrl(url: string) {
        if (url.startsWith("http")) return url;
        return this.baseUrl + url;
    }

    // requests here
    getTransaction(params: string, bak: string, country?: string, isBusiness?: string): Promise<any> {
        return this.report(this.transport.post("/api/v1/transaction/managed/query", {
            bak,
            params,
            country,
            ib: isBusiness,
        }), ({ sanitized }: SanitizedResponse<ITransaction>) => {
            // TODO deobfuscate here
            if (!sanitized.pd?.card) {
                sanitized.payment_options.splice(sanitized.payment_options.findIndex((p) => p.method === PaymentMethod.CARD), 1);
                if (sanitized.configuration.default_payment_method === PaymentMethod.CARD && sanitized.payment_options.length) {
                    sanitized.configuration.default_payment_method = sanitized.payment_options[0].method;
                }
            }
        });
    }

    generateNonce(businessCode: string, serviceCode: string, cardData: string): Promise<any> {
        return this.report(this.transport.post(`/api/v1/card/nonce`, {
            card_data: cardData,
            service_code: serviceCode,
            business_code: businessCode,
            session_id: Utils.randomNumber(1, 30).toString(),
            dobfalgo: "BASE64_DECODE((COUNT(EOI-50, EOI),MOVE(SOI))->JSON_PARSE",
        }));
    }

    initiateCharge(transaction: ITransaction, businessId: string, method: PaymentMethod, extraPayload: NoseurObject<any> = {}): Promise<any> {
        return this.report(this.transport.post("/api/v1/transaction/charge/managed", {
            method,
            business_id: businessId,
            reference: transaction.reference,
            mcrk: this.expressClientRequestKey,
            ...extraPayload
        }));
    }

    generateOtp(reference: string, businessId: string, chargeData: string): Promise<any> {
        return this.report(this.transport.post(`/api/v1/transaction/charge/managed/otp/generate`, {
            reference,
            business_id: businessId,
            charge_data: chargeData,
            mcrk: this.expressClientRequestKey,
        }));
    }

    validateOtp(reference: string, businessId: string, chargeData: string, otpId: string, otp: string): Promise<any> {
        return this.report(this.transport.post(`/api/v1/transaction/charge/managed/otp/validate`, {
            otp,
            reference,
            otp_id: otpId,
            business_id: businessId,
            charge_data: chargeData,
            mcrk: this.expressClientRequestKey,
        }));
    }

    simulateChargePayment(chargeData: string, payload: NoseurObject<any> = {}): Promise<any> {
        if (process.env.REACT_APP_ENV === "production") {
            throw new Error("Suspicious request detected, incident will be reported"); // TODO report to soteria and ban ip for 30 days
        }
        return this.report(this.transport.post(`/api/v1/transaction/charge/payment/${encodeURIComponent(encodeURIComponent(chargeData))}`, payload));
    }

    static reportError<T>(response: SanitizedResponse<T>) {
        Utils.toastError(response?.errorMessage ?? `${response}`);
    }

}

RequestProcessor.register(RequestType.JSON, (data: object) => {
    return {
        contentType: "application/json",
        buffer: KyofuucUtils.safeStringify(data),
    } as any;
});

// TODO fix issue with Buffer in kyofuuc -> https://stackoverflow.com/a/71953677/6626422